import type { ActivityStatusBucket } from "@/modules/dashboard/payments-activity/types/paymentsActivity.types";
import type { PaymentsLedgerTranslations } from "@/shared/lang/interfaces/paymentsLedger";

import {
  LedgerAccountType,
  LedgerOperationType,
  type LedgerOperationKind,
} from "../types/paymentsLedger.types";

/**
 * The `operation_type` the ledger stamps on the mirror entry of a reversal. `LedgerService.reverse` inserts
 * it with the ORIGINAL `operation_id` but this type — so `?operation_type=PAYOUT` never returns the reversals
 * of a payout, despite what the endpoint's Swagger summary claims. Every read of an operation's ledger
 * therefore issues two requests (its own type + this one) and merges them by `seq`.
 */
export const LEDGER_REVERSAL_TYPE = LedgerOperationType.REVERSE;

/** Explorer tab → the `operation_type` the ledger endpoint expects for that kind of operation. */
export const LEDGER_TYPE_BY_KIND: Record<LedgerOperationKind, "PAYIN" | "PAYOUT" | "SWAP"> = {
  payouts: LedgerOperationType.PAYOUT,
  payins: LedgerOperationType.PAYIN,
  swaps: LedgerOperationType.SWAP,
};

/** Reverse of `LEDGER_TYPE_BY_KIND` — used to turn a wallet statement leg into a link to this view. */
export const LEDGER_KIND_BY_TYPE: Partial<Record<LedgerOperationType, LedgerOperationKind>> = {
  [LedgerOperationType.PAYOUT]: "payouts",
  [LedgerOperationType.PAYIN]: "payins",
  [LedgerOperationType.SWAP]: "swaps",
};

export const LEDGER_OPERATION_KINDS: readonly LedgerOperationKind[] = ["payouts", "payins", "swaps"];

/** Page size of the operation explorer. Backend list endpoints default to 10 and cap at 200. */
export const LEDGER_PICKER_PAGE_SIZE = 15;

/**
 * Refresh cadence of the ledger pane. There is no websocket for payments, and an operation only gains entries
 * when it moves (a settle appends one) — so this ticks ONLY while the selected operation is non-terminal.
 * Matches the payments-activity list cadence.
 */
export const LEDGER_POLL_INTERVAL_MS = 15_000;

/** Mirrors coloca-nest `SwapStatus`. A swap is atomic: it is created and completed in one transaction. */
export const SwapRuntimeStatus = {
  CREATED: "CREATED",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
} as const;
export type SwapRuntimeStatus = (typeof SwapRuntimeStatus)[keyof typeof SwapRuntimeStatus];

/** Swap status → display bucket. Its own map: the swap vocabulary is not the payin/payout one. */
export const SWAP_STATUS_BUCKET: Record<SwapRuntimeStatus, ActivityStatusBucket> = {
  [SwapRuntimeStatus.CREATED]: "pending",
  [SwapRuntimeStatus.COMPLETED]: "settled",
  [SwapRuntimeStatus.FAILED]: "failed",
};

/** Only CREATED can still move — and only inside the redeem transaction, so it is rare to ever observe it. */
export const SWAP_NON_TERMINAL: ReadonlySet<string> = new Set([SwapRuntimeStatus.CREATED]);

export const SWAP_STATUS_LABEL_KEYS: Partial<Record<string, keyof PaymentsLedgerTranslations>> = {
  [SwapRuntimeStatus.CREATED]: "status_swap_created",
  [SwapRuntimeStatus.COMPLETED]: "status_swap_completed",
  [SwapRuntimeStatus.FAILED]: "status_swap_failed",
};

/**
 * Accounting catalog mirrored from coloca-nest `LEDGER_ACCOUNTS` (`constants/ledger-accounts.catalog.ts`).
 *
 * It is a deliberate frontend mirror, for the same reason the provider-capability catalog is one: the backend
 * publishes no endpoint listing its chart of accounts, and `account.name` comes back in English ("Client
 * liability"), which must not reach a Spanish or Portuguese user. An account code the mirror does not know
 * still renders — it falls back to `account.name` and is flagged as unrecognised, never hidden.
 */
export const LEDGER_ACCOUNT_LABEL_KEYS: Record<string, keyof PaymentsLedgerTranslations> = {
  "1100-CASH-AT-PROVIDER": "account_cash_at_provider",
  "1200-COLOCA-TREASURY": "account_coloca_treasury",
  "2100-CLIENT-LIABILITY": "account_client_liability",
  "2200-PAYOUT-IN-TRANSIT": "account_payout_in_transit",
  "2300-PAYIN-SUSPENSE": "account_payin_suspense",
  "2400-FX-POSITION": "account_fx_position",
  "3100-FEE-REVENUE": "account_fee_revenue",
  "4100-PROVIDER-COST": "account_provider_cost",
};

/** One-line explanation of what each account holds — shown as the account's tooltip. */
export const LEDGER_ACCOUNT_HINT_KEYS: Record<string, keyof PaymentsLedgerTranslations> = {
  "1100-CASH-AT-PROVIDER": "account_cash_at_provider_hint",
  "1200-COLOCA-TREASURY": "account_coloca_treasury_hint",
  "2100-CLIENT-LIABILITY": "account_client_liability_hint",
  "2200-PAYOUT-IN-TRANSIT": "account_payout_in_transit_hint",
  "2300-PAYIN-SUSPENSE": "account_payin_suspense_hint",
  "2400-FX-POSITION": "account_fx_position_hint",
  "3100-FEE-REVENUE": "account_fee_revenue_hint",
  "4100-PROVIDER-COST": "account_provider_cost_hint",
};

/** Mirrors coloca-nest `taxLedgerCode`: one account per tax, `{prefix}{country}-{code}`. */
export const LEDGER_TAX_ACCOUNT_PREFIXES: ReadonlyArray<{
  prefix: string;
  label: keyof PaymentsLedgerTranslations;
  hint: keyof PaymentsLedgerTranslations;
}> = [
  { prefix: "2500-TAX-PAYABLE-", label: "account_tax_payable", hint: "account_tax_payable_hint" },
  { prefix: "4300-TAX-EXPENSE-", label: "account_tax_expense", hint: "account_tax_expense_hint" },
];

export const LEDGER_ACCOUNT_TYPE_LABEL_KEYS: Record<string, keyof PaymentsLedgerTranslations> = {
  [LedgerAccountType.ASSET]: "account_type_asset",
  [LedgerAccountType.LIABILITY]: "account_type_liability",
  [LedgerAccountType.REVENUE]: "account_type_revenue",
  [LedgerAccountType.EXPENSE]: "account_type_expense",
  [LedgerAccountType.SUSPENSE]: "account_type_suspense",
};

/**
 * Entry `description` → label, by EXACT match on what the runtime writes today (`payin.service.ts`,
 * `payout.service.ts`, `swap.service.ts`). The description is free text, so a value that is not in this map
 * renders verbatim rather than being forced into a wrong label.
 */
export const LEDGER_ENTRY_LABEL_KEYS: Record<string, keyof PaymentsLedgerTranslations> = {
  "payout reserve": "entry_payout_reserve",
  "payout settle": "entry_payout_settle",
  "payin detected": "entry_payin_detected",
  "payin confirmed": "entry_payin_confirmed",
  swap: "entry_swap",
  "coloca top up": "entry_coloca_top_up",
  "coloca refund": "entry_coloca_refund",
};

/**
 * Prefix rules for the descriptions the runtime builds with interpolation — today only the cross-currency
 * payout release (`payout release (${reason}); fx retained`).
 */
export const LEDGER_ENTRY_LABEL_PREFIXES: readonly {
  prefix: string;
  key: keyof PaymentsLedgerTranslations;
}[] = [{ prefix: "payout release", key: "entry_payout_release" }];

/** Last resort when the description is empty or unknown: name the entry by its `operation_type`. */
export const LEDGER_ENTRY_FALLBACK_KEYS: Record<LedgerOperationType, keyof PaymentsLedgerTranslations> = {
  [LedgerOperationType.PAYIN]: "entry_generic_payin",
  [LedgerOperationType.PAYOUT]: "entry_generic_payout",
  [LedgerOperationType.SWAP]: "entry_generic_swap",
  [LedgerOperationType.FEE]: "entry_generic_fee",
  [LedgerOperationType.ADJUST]: "entry_generic_adjust",
  [LedgerOperationType.REVERSE]: "entry_generic_reverse",
};

/** Canonical uuid shape — the ledger endpoint 400s on anything else, so the UI never sends it. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string): boolean {
  return UUID_RE.test(value.trim());
}

export function isLedgerOperationKind(value: string | undefined): value is LedgerOperationKind {
  return value === "payouts" || value === "payins" || value === "swaps";
}
